import { Component } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { GridApi, CellEditingStoppedEvent, ValueFormatterParams, ColDef, ICellRendererParams, ValueGetterParams } from 'ag-grid-community';
import { FormGroup, FormBuilder, FormControl, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { ShipmentOrderCreatorComponent } from '../shipment-order-creator/shipment-order-creator.component'
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { IconCellRendererComponent } from '../../icon-cell-renderer/icon-cell-renderer.component';
import { ContactTypeEditorComponent } from '../../contacts/contact-type-editor/contact-type-editor.component'
import { Router } from '@angular/router';
import { ShipmentListsModalComponent } from '../shipment-lists-modal/shipment-lists-modal.component';
import { NgZone } from '@angular/core';

@Component({
  selector: 'app-shipment-details',
  templateUrl: './shipment-details.component.html',
  styleUrls: ['./shipment-details.component.scss']
})
export class ShipmentDetailsComponent implements ICellRendererAngularComp {

  public params: any;
  private gridApi!: GridApi;

  constructor(private http: HttpClient, private formBuilder: FormBuilder, private dialog: MatDialog, private router: Router, private zone: NgZone) { }

  // variable to trigger when to display HTML content after API loads
  showContent: boolean = false

  // initialize UUID
  shipment_list_uuid: string = ''
  trip_list_uuid: string = ''

  // initialize alternate_reference no. for printing functions
  alternate_reference: string = ''

  // initialize objects to store details of shipment details
  shipment: any = {};
  shipment_details: any = {}
  operation: any = {}
  carrier: any = {}
  consignee_accounting: any = {}
  pallets: Array<{ [key: string]: string | number | object | boolean }> = [];
  barcodes: Array<{ [key: string]: string | number | object | boolean }> = [];
  services: Array<{ [key: string]: string | number | object | boolean }> = [];

  // forms for displaying details
  detailsForm!: FormGroup;

  // form for adding new pallet
  newPalletForm!: FormGroup;

  // define arrays for storing mat-select options
  typeListBarcodeStatus = [
    '',
    'MANUAL AUDIT',
    'MISSING',
    'OVERAGE',
    'OPENED',
    'BROKEN',
    'CRUSHED',
    'WET',
    'HOLDOVER'
  ]

  typeListCartonResolution = [
    '',
    'DELIVERED AS IS',
    'HELD FOR DISPOSITION',
    'RETAPED AND DELIVERED',
    'REPACKAGED AND DELIVERED',
    'DISCARDED DISPOSED'
  ]

  typeListPallet = [
    "01",
    "02",
    "03"
  ]

  // default column definitions
  defaultColDef = {
    sortable: true,
    filter: 'agTextColumnFilter',
    filterParams: {
      caseSensitive: false,
    },
    autoHeight: true,
    cellClass: 'cell-wrap-text',
    resizable: true,
    editable: true
  };

  // headers for the pallets grid
  palletsColumnDefs = [
    {
      field: 'count',
      headerName: 'Count'
    },    
    { field: 'load_no', headerName: 'Load No.' },
    { field: 'length', headerName: 'Length' },
    { field: 'width', headerName: 'Width' },
    { field: 'height', headerName: 'Height' },
    { field: 'weight', headerName: 'Weight' },
    { field: 'cartons_pallet', headerName: 'Carton Count' },
    {
      headerName: 'Action', cellRenderer: (params: any) => {
        const button = document.createElement('button');
        const icon = document.createElement('i');

        // Add Font Awesome classes to the icon element
        icon.classList.add('fas', 'fa-trash'); 

        // Append the icon to the button
        button.appendChild(icon);
        button.addEventListener('click', () => {
          this.zone.run(() => {
            this.deletePallet(params);
          });
        });
        return button;
      }, editable: false, width: 100,
    },    
    {
      headerName: 'Action', cellRenderer: (params: any) => {
        const button = document.createElement('button');
        const icon = document.createElement('i');

        // Add Font Awesome classes to the icon element
        icon.classList.add('fas', 'fa-edit'); 

        // Append the icon to the button
        button.appendChild(icon);
        button.addEventListener('click', () => {
          this.zone.run(() => {
            this.editPallet(params);
          });
        });
        return button;
      }, editable: false, width: 100,
    },        
  ]

  // headers for the barcodes grid
  barcodeColumnDefs = [
    {
      headerName: 'Count',
      valueGetter: this.countGetter
    },
    {
      field: 'package_uuid',
      headerName: 'Pallet No.',
      valueFormatter: this.getPalletNumber.bind(this)
    },
    { field: 'barcode', headerName: 'Barcode' },
    {
      field: 'received_in',
      headerName: 'Received In',
      valueFormatter: function (params: ValueFormatterParams) {
        return params.value === "false" ? '❌' : '✅';
      }
    },
    {
      field: 'audited',
      headerName: 'Audited',
      valueFormatter: function (params: ValueFormatterParams) {
        return params.value === "false" ? '❌' : '✅';
      }
    },
    {
      field: 'delivered',
      headerName: 'Delivered',
      valueFormatter: function (params: ValueFormatterParams) {
        return params.value === "false" ? '❌' : '✅';
      }
    }, 
    {
      field: 'delivered_method',
      headerName: 'Delivered Method',
    },         
    {
      field: 'delivered_time',
      headerName: 'Delivered Time',
    },       
    {
      field: 'return_status',
      headerName: 'Status',
      cellEditor: ContactTypeEditorComponent,
      cellEditorPopup: true,
      cellEditorParams: {
        multiple: false,
        types: this.typeListBarcodeStatus
      }
    },
    {
      field: 'carton_resolution',
      headerName: 'Carton Resolution',
      cellEditor: ContactTypeEditorComponent,
      cellEditorPopup: true,
      cellEditorParams: {
        multiple: false,
        types: this.typeListCartonResolution
      }
    },
    {
      headerName: 'Action', cellRenderer: (params: any) => {
        const button = document.createElement('button');
        const icon = document.createElement('i');

        // Add Font Awesome classes to the icon element
        icon.classList.add('fas', 'fa-trash'); 

        // Append the icon to the button
        button.appendChild(icon);
        button.addEventListener('click', () => {
          this.zone.run(() => {
            this.deleteBarcode(params);
          });
        });
        return button;
      }, editable: false, width: 100,
    },
  ]

  // headers for the service rates grid
  serviceRatesColumnDefs = [
    {
      field: 'service',
      headerName: 'Service',
    },
    {
      field: 'service_type',
      headerName: 'Service Type',
    },
    { field: 'flat_rate', headerName: 'Flat Rate ($)' },
    {
      headerName: 'Action', cellRenderer: (params: any) => {
        const button = document.createElement('button');
        const icon = document.createElement('i');

        // Add Font Awesome classes to the icon element
        icon.classList.add('fas', 'fa-trash'); 

        // Append the icon to the button
        button.appendChild(icon);
        button.addEventListener('click', () => {
          this.zone.run(() => {
            this.deleteServiceRate(params);
          });
        });
        return button;
      }, editable: false,
    },
    {
      headerName: 'Action', cellRenderer: (params: any) => {
        const button = document.createElement('button');
        const icon = document.createElement('i');

        // Add Font Awesome classes to the icon element
        icon.classList.add('fas', 'fa-edit'); 

        // Append the icon to the button
        button.appendChild(icon);
        button.addEventListener('click', () => {
          this.zone.run(() => {
            this.editServiceRate(params);
          });
        });
        return button;
      }, editable: false,
    },    
  ]

  editShipmentDetails() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '650px'; // You can use pixels or other units
    dialogConfig.height = '650px';
    dialogConfig.data = {
      data: {
        modalTitle: 'Edit Shipment Details',
        modalMessage: {uuid: this.shipment_list_uuid, shipment: this.shipment},
      },
    };
    this.dialog.open(ShipmentListsModalComponent, dialogConfig);
  }

  editServiceRate(params: any) {
    const dialogConfig = new MatDialogConfig();
    const contact_profile_default_service_uuid = params.data.contact_profile_default_service_uuid;
    const service = params.data.service;
    const service_type = params.data.service_type;        
    const flat_rate = params.data.flat_rate;        

    dialogConfig.width = '10'; // You can use pixels or other units
    dialogConfig.height = '10';
    dialogConfig.data = {
      data: {
        modalTitle: 'Edit Default Services',
        modalMessage: {uuid: contact_profile_default_service_uuid, service: service, service_type: service_type, flat_rate: flat_rate},
      },
    };

    console.log('Dialog Config:', dialogConfig);

    this.dialog.open(ShipmentListsModalComponent, dialogConfig);
  }

  deleteServiceRate(params: any) {
    const dialogConfig = new MatDialogConfig();
    const contact_profile_default_service_uuid = params.data.contact_profile_default_service_uuid;
    const service = params.data.service;
    const service_type = params.data.service_type;        

    dialogConfig.width = '10'; // You can use pixels or other units
    dialogConfig.height = '10';
    dialogConfig.data = {
      data: {
        modalTitle: 'Delete Service Rate Confirmation',
        modalMessage: {uuid: contact_profile_default_service_uuid, service: service, service_type: service_type},
      },
    };

    console.log('Dialog Config:', dialogConfig);

    this.dialog.open(ShipmentListsModalComponent, dialogConfig);
  }

  addBarcode() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '348px'; // You can use pixels or other units
    dialogConfig.height = '330px';
    dialogConfig.data = {
      data: {
        modalTitle: 'Add Barcode',
        modalMessage: {pallets: this.pallets, shipment_list_uuid: this.shipment_list_uuid},
      },
    };
    this.dialog.open(ShipmentListsModalComponent, dialogConfig);
  }

  updateBarcodePallet() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '348px'; // You can use pixels or other units
    dialogConfig.height = '180px';
    dialogConfig.data = {
      data: {
        modalTitle: 'Update Work Order Barcodes/Pallet',
        modalMessage:  {shipment_list_uuid: this.shipment_list_uuid},
      },
    };
    this.dialog.open(ShipmentListsModalComponent, dialogConfig);
  }

  changeBarcode() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '348px'; // You can use pixels or other units
    dialogConfig.height = '50px';
    dialogConfig.data = {
      data: {
        modalTitle: 'Change Barcodes Status',
        modalMessage:  {shipment_list_uuid: this.shipment_list_uuid},
      },
    };
    this.dialog.open(ShipmentListsModalComponent, dialogConfig);
  }

  moveBarcode() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '348px'; // You can use pixels or other units
    dialogConfig.height = '180px';
    dialogConfig.data = {
      data: {
        modalTitle: 'Move Barcode Onto Pallet',
        modalMessage:  {shipment_list_uuid: this.shipment_list_uuid},
      },
    };
    this.dialog.open(ShipmentListsModalComponent, dialogConfig);
  }

  deleteBarcode(params: any) {
    const dialogConfig = new MatDialogConfig();
    const product_uuid = params.data.product_uuid;
    const barcode = params.data.barcode;

    dialogConfig.width = '10'; // You can use pixels or other units
    dialogConfig.height = '10';
    dialogConfig.data = {
      data: {
        modalTitle: 'Delete Barcode Confirmation',
        modalMessage: {uuid: product_uuid, barcode: barcode},
      },
    };

    console.log('Dialog Config:', dialogConfig);

    this.dialog.open(ShipmentListsModalComponent, dialogConfig);
  }

  addPallet() {
    const dialogConfig = new MatDialogConfig();
    
    dialogConfig.width = '348px'; // You can use pixels or other units
    dialogConfig.height = '495px';
    dialogConfig.data = {
      data: {
        modalTitle: 'Add Pallet',
        modalMessage:  {shipment_list_uuid: this.shipment_list_uuid, trip_list_uuid: this.trip_list_uuid, reload: 1},
      },
    };

    console.log('Dialog Config:', dialogConfig);

    this.dialog.open(ShipmentListsModalComponent, dialogConfig);
  }

  addMultiplePallets() {
    const dialogConfig = new MatDialogConfig();
    
    dialogConfig.width = '348px'; // You can use pixels or other units
    dialogConfig.height = '585px';
    dialogConfig.data = {
      data: {
        modalTitle: 'Add Multiple Pallets',
        modalMessage:  {shipment_list_uuid: this.shipment_list_uuid, trip_list_uuid: this.trip_list_uuid, reload: 1},
      },
    };

    console.log('Dialog Config:', dialogConfig);

    this.dialog.open(ShipmentListsModalComponent, dialogConfig);
  }

  editPallet(params: any) {
    const dialogConfig = new MatDialogConfig();
    const package_uuid = params.data.package_uuid;
    const cartons_pallet = params.data.cartons_pallet;    
    const height = params.data.height;
    const length = params.data.length;
    const width = params.data.width;
    const weight = params.data.weight;
    const load_no = params.data.load_no;
    const count = params.data.count;    

    console.log('Edit Pallet:', params.data);      
        

    dialogConfig.width = '10'; // You can use pixels or other units
    dialogConfig.height = '10';
    dialogConfig.data = {
      data: {
        modalTitle: 'Edit Pallet',
        modalMessage: {package_uuid: package_uuid, 
                      count: count, 
                      cartons_pallet: cartons_pallet, 
                      height: height, 
                      length: length,
                      width: width,
                      weight: weight,
                      load_no: load_no,
                      reload: 1},
      },
    };

    console.log('Dialog Config:', dialogConfig);

    this.dialog.open(ShipmentListsModalComponent, dialogConfig);
  }

  deletePallet(params: any) {
    const dialogConfig = new MatDialogConfig();
    const package_uuid = params.data.package_uuid;
    const count = params.data.count;    

    console.log('Delete Pallet:', params.data);      
        

    dialogConfig.width = '10'; // You can use pixels or other units
    dialogConfig.height = '10';
    dialogConfig.data = {
      data: {
        modalTitle: 'Delete Pallet Confirmation',
        modalMessage: {uuid: package_uuid, count: count, reload: 1},
      },
    };

    console.log('Dialog Config:', dialogConfig);

    this.dialog.open(ShipmentListsModalComponent, dialogConfig);
  }

  addServiceRate() {

    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '10px'; // You can use pixels or other units
    dialogConfig.height = '10px';
    dialogConfig.data = {
      data: {
        modalTitle: 'Add Service Rate',
        modalMessage: this.services[0]['contact_uuid'],
      },
    };
    this.dialog.open(ShipmentListsModalComponent, dialogConfig);
  }

  // function to set the count of barcodes in the shipment list using their row index
  private countGetter(params: ValueGetterParams) {
    if (params.node && params.node.rowIndex !== null) {
      return params.node.rowIndex + 1
    }
    return ''
  }

  // function to retrieve the appropriate Pallet Number of a barcode (carton) by
  // comparing the package_uuids by looping through the array of pallets and retrieving the index+1
  private getPalletNumber(params: ValueFormatterParams): string {
    if (params.data && params.node && params.node.rowIndex !== null) {
      const packageUuid = params.data.package_uuid;
      const index = this.pallets.findIndex(pallet => pallet['package_uuid'] === packageUuid);
      return index !== -1 ? String(index + 1) : '';
    }
    return '';
  }


  agInit(params: any): void {

    // define the targeted shipment list by getting its shipment_list_uuid
    this.params = params;
    this.gridApi = params.api;
    this.shipment_list_uuid = params.data.shipment_list_uuid

    console.log(params)
    console.log(this.shipment_list_uuid)

    const shipment_details_url = `/api/shipment_lists/shipment_details.php?uuid=${this.shipment_list_uuid}`;

    // GET request to the contacts API call for specific contact_UUID
    this.http.get<any>(shipment_details_url).subscribe(response => {
      
      this.shipment = response;
      this.shipment_details = response.details
      console.log(this.shipment_details)
      this.trip_list_uuid = this.shipment_details.trip_list_uuid
      this.alternate_reference = this.shipment_details.alternate_reference

      if (response.pallets) {
        this.pallets = response.pallets
      }

      if (response.barcode) {
        this.barcodes = response.barcode
      }

      if (response.carrier) {
        this.carrier = response.carrier
      }

      if (response.operation) {
        this.operation = response.operation
      }

      if (response.services) {
        this.services = response.services
      }

      if (response.consignee_accounting) {
        this.consignee_accounting = response.consignee_accounting
      }

      this.checkFormReady();
    })
  }

  // check if API is finished loading -> populate all the different forms using formbuilder
  // and set show content to true at the end to render HTML
  private checkFormReady(): void {

    if (
      this.pallets &&
      this.shipment_details &&
      this.barcodes &&
      this.services &&
      this.operation &&
      this.consignee_accounting &&
      this.carrier
    ) {

      this.detailsForm = this.formBuilder.group({

        date: [this.shipment_details.date],
        asn_number: [this.shipment_details.asn_number],
        trip_number: [this.shipment_details.trip_number],
        external_bill_of_lading: [this.shipment_details.external_bill_of_lading],
        store_number: [this.shipment_details.store_number],
        total_cartons: [this.shipment_details.total_cartons],
        reported_weight: [this.shipment_details.reported_weight],
        status: [this.shipment_details.status],
        week_no: [this.shipment_details.week_no],
        alternate_reference: [this.shipment_details.alternate_reference],
        manifest_id: [this.shipment_details.manifest_id],
        appointment_day: [this.operation.appointment_day],
        appointment_start: [this.operation.appointment_start],
        appointment_end: [this.operation.appointment_end],
        contact_organization: [this.carrier.contact_organization],
        express_road: [this.consignee_accounting.express_road],
        ab_carbon_fuel_tax: [this.consignee_accounting.ab_carbon_fuel_tax],
        bc_carbon_fuel_tax: [this.consignee_accounting.bc_carbon_fuel_tax]

      })

      this.newPalletForm = this.formBuilder.group({

        cartons_pallet: ['', Validators.required],
        dimension_height: ['', Validators.required],
        dimension_width: ['', Validators.required],
        dimension_length: ['', Validators.required],
        load_weight: ['', Validators.required],
        load_no: ['', Validators.required]

      })

    }

    this.showContent = true;
  }

  // function to add new pallet to the shipment list
  submitNewPallet(): void {
    const formData = this.newPalletForm.value;
    formData.shipment_list_uuid = this.shipment_list_uuid;
    formData.trip_list_uuid = this.trip_list_uuid;
    const shipment_details_url = `/api/shipment_lists/shipment_details.php?uuid=${this.shipment_list_uuid}`;

    let url = `/api/shipment_lists/shipment_details.php?table=shipment_and_receiving&command=ADD_PACKAGE`;

    this.http.post<any>(url, formData)
      .subscribe(response => {
        console.log('Post Response: ', response)
        this.newPalletForm.reset()
        this.http.get<any>(shipment_details_url).subscribe(response => {

          console.log(response)

          this.pallets = response.pallets

        })
      })


    console.log('Pallet Form', formData)
    console.log('Stringify:', JSON.stringify(formData))
  }

  refresh(params: any): boolean {
    return false;
  }

  // opens a modal to display the shipment list work order wizard
  createWorkOrder(): void {
    const dialogRef: MatDialogRef<ShipmentOrderCreatorComponent> = this.dialog.open(ShipmentOrderCreatorComponent, {
      width: '90%',
      height: '90%',
      data: { shipment_list_uuid: this.shipment_list_uuid }
    });

    // Subscribe to the afterClosed event to handle any actions after the modal is closed
    dialogRef.afterClosed().subscribe((result) => {

    });
  }

  // opens a modal to display the shipment list work order wizard
  createEDIFile(lading_uuid: string, alternate_reference: string): void {
    const dialogConfig = new MatDialogConfig();     

    dialogConfig.width = '10'; // You can use pixels or other units
    dialogConfig.height = '10';
    dialogConfig.data = {
      data: {
        modalTitle: 'Create EDI File',
        modalMessage: {lading_uuid: lading_uuid, alternate_reference: alternate_reference},
      },
    };

    this.dialog.open(ShipmentListsModalComponent, dialogConfig);
  }

  // function to allow for cell editing in the grid, POST request is made when cell editing is finished
  onCellEditingStopped(event: CellEditingStoppedEvent) {

    const editedValue = event.value;
    const column = event.column;
    const rowNode = event.node;

    const product_uuid = rowNode.data.product_uuid
    const return_status = rowNode.data.return_status
    const carton_resolution = rowNode.data.carton_resolution

    const barcode_status_url = `/api/shipment_lists/shipment_details.php?table=shipment_and_receiving&command=UPDATE_BARCODE_STATUS`;
    const carton_resolution_url = `/api/shipment_lists/shipment_details.php?table=shipment_and_receiving&command=PRODUCT_STATUS_UPDATE`;

    const columnName = column.getColId();

    console.log('Edited Value:', editedValue);
    console.log('Row Data:', rowNode.data);
    console.log('Column Name:', columnName)

    // create POST body of cell edited of column, columnName, and value changed, editedValue
    var postData = {
      [columnName]: editedValue,
      product_uuid: product_uuid
    }

    // if an edit is made in the status section of the barcodes subgrid
    if (columnName === 'return_status') {

      postData = {
        [columnName]: editedValue,
        product_uuid: product_uuid,
      }

      console.log('Post Data:', postData);
      console.log('Stringify:', JSON.stringify(postData))

      return this.http.post<any>(barcode_status_url, postData)
        .subscribe(response => {
          console.log('Post Response: ', response)
        })

      // if an edit is made in the carton resolution section of the barcodes grid
    } else if (columnName === 'carton_resolution') {

      postData = {
        [columnName]: editedValue,
        product_uuid: product_uuid,
        return_status: return_status
      }

      console.log('Post Data:', postData);
      console.log('Stringify:', JSON.stringify(postData))

      return this.http.post<any>(carton_resolution_url, postData)
        .subscribe(response => {
          console.log('Post Response: ', response)
        })
    } else {
      return
    }
  }

  // print function checks the string passed to determine which document is printed in the new tab
  //printLabel(doc_type: string, uuid: string) {
  //  const accessToken = localStorage.getItem('access_token');
  //  let link: string = '';
  //
  //  if (doc_type === 'packing') {
  //    link = `/api/work_orders/print_pdfs/newams_print_packing_label.php?uuid=${uuid}&token=${accessToken}`;
  //  }
  //  else if (doc_type === 'pallet') {
  //    link = `/api/work_orders/print_pdfs/newams_print_pallet_label.php?uuid=${uuid}&token=${accessToken}`;
  //  }
  //  else if (doc_type === 'bol') {
  //    console.log(this.alternate_reference)
  //    link = `/api/work_orders/print_pdfs/newams_print_bol.php?uuid=${uuid}&token=${accessToken}`;
  //  }
  //  else if (doc_type === 'trailer') {
  //    link = `/api/work_orders/print_pdfs/newams_print_trailer_label.php?uuid=${uuid}&token=${accessToken}`;
  //  }
  //
  //  window.open(link, "_blank")
  //}

  // print function checks the string passed to determine which document is printed in the new tab
  printLabel(doc_type: string, uuid: string) {
    let endpoint = '';
  
    switch (doc_type) {
      case 'packing':
        endpoint = `/api/work_orders/print_pdfs/newams_print_packing_label.php`;
        break;
      case 'pallet':
        endpoint = `/api/work_orders/print_pdfs/newams_print_pallet_label.php`;
        break;
      case 'bol':
        endpoint = `/api/work_orders/print_pdfs/newams_print_bol.php`;
        break;
      case 'trailer':
        endpoint = `/api/work_orders/print_pdfs/newams_print_trailer_label.php`;
        break;
      default:
        return;
    }
  
    this.http.get(endpoint, {
      responseType: 'blob', // Expect binary data
      params: { uuid: uuid }
    }).subscribe(blob => {
      const url = window.URL.createObjectURL(blob);
      window.open(url);
      // Optionally revoke the object URL after some time to free memory
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
    });    
  }

  // print function checks the string passed to determine which document is printed in the new tab
  printBOL(uuid: string, alternate_reference: string, shipper_contact_uuid: string) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '348px'; // You can use pixels or other units
    dialogConfig.height = '405px';
    dialogConfig.data = {
      data: {
        modalTitle: 'Print Bill Of Lading',
        modalMessage: {lading_uuid: uuid, alternate_reference: alternate_reference, shipper_contact_uuid: shipper_contact_uuid},
      },
    };
    this.dialog.open(ShipmentListsModalComponent, dialogConfig);    
  }  

  updateBarcode() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '348px'; // You can use pixels or other units
    dialogConfig.height = '180px';
    dialogConfig.data = {
      data: {
        modalTitle: 'Update Work Order Barcodes/Pallet',
        modalMessage: {shipment_list_uuid: this.shipment_list_uuid},
      },
    };
    this.dialog.open(ShipmentListsModalComponent, dialogConfig);
  }

  viewShipmentDetails() {
    this.router.navigate(['shipment_details', this.shipment_list_uuid],);
  }

  onCellEditingRatesStopped(event: CellEditingStoppedEvent) {

    const editedValue = event.value;
    const column = event.column;
    const rowNode = event.node;

    const contact_profile_default_service_uuid = rowNode.data.contact_profile_default_service_uuid;

    const edit_service_rate_url = `/api/shipment_lists/shipment_details.php?table=shipment_and_receiving&command=EDIT_SERVICE_RATE`;

    const columnName = column.getColId();

    console.log('Edited Value:', editedValue);
    console.log('Row Data:', rowNode.data);
    console.log('Column Name:', columnName);

    var postData = {
      [columnName]: editedValue,
      contact_profile_default_service_uuid: contact_profile_default_service_uuid
    }

    console.log('Post Data:', postData);
    console.log('Stringify:', JSON.stringify(postData))

    return this.http.post<any>(edit_service_rate_url, postData)
      .subscribe(response => {
        console.log('Post Response: ', response)
      })
  }

}
